import type { ComponentProps } from 'react';
import { useRouter } from 'next/router';
import { sendPostMessage } from '@/clientOnlyUtils/messagingUtils';
import React from 'react';

import { Button } from '@poseidon/components/Button';
import { Alerts, General } from '@poseidon/components/StrokeIcons';

import type { PoseidonApiAuthRes } from '~/auth/auth.context';
import { Breadcrumb, Breadcrumbs } from '@/components/Breadcrumbs';
import { ButtonLink } from '@/components/Button';
import ContentDivider from '@/components/ContentDivider';
import PageHeader from '@/components/PageHeader';
import { WaveSpinnerContainer } from '@/components/WaveSpinnerContainer';
import { useTeamCreateMutation } from '@/hooks/trpc/useTeamMutation';
import { api } from '@/utils/api';
import { useAuthContext } from '~/auth/auth.context';
import { ContentSection } from '~/components/ContentSection/ContentSection';
import { PaddedScrollable } from '~/components/PaddedScrollable';
import { TeamsTable } from '~/components/TeamsTable';

export type PoseidonAuthData = NonNullable<PoseidonApiAuthRes['userTeam']>;
export type PoseidonSuperAdminAuthData = PoseidonAuthData & { isSuper: true };

export const AuthedPageWithTeam = (props: { children: (authData: PoseidonAuthData) => React.JSX.Element }) => {
  const [{ isPending, ...authData }] = useAuthContext();
  const router = useRouter();

  React.useEffect(() => {
    const routeChangeComplete = (url: string) => {
      sendPostMessage('URL_CHANGE', { url });
    };
    router.events.on('routeChangeComplete', routeChangeComplete);

    return () => {
      router.events.off('routeChangeComplete', routeChangeComplete);
    };
  }, [router]);

  // not logged in, push to login page
  React.useEffect(() => {
    if (!isPending && authData.user == null) {
      void router.push({ pathname: '/login', query: { redirect: router.asPath } });
    }
  }, [authData.user, isPending, router]);

  if (isPending) {
    return <WaveSpinnerContainer message="Authenticating" />;
  }

  if (authData.user == null) {
    return <WaveSpinnerContainer message="Not logged in, sending to login page" />;
  }

  if (authData.userTeam != null) {
    return props.children(authData.userTeam);
  }

  // user is authenticated but not part of a team
  return <TeamPicker />;
};

type RedirectableProps = ComponentProps<'div'> & {
  redirect?: string;
};

export const SuperAdminAuthedPageWithTeam = (props: {
  children: (authData: PoseidonSuperAdminAuthData) => React.JSX.Element;
}) => {
  const [{ isPending, ...authData }] = useAuthContext();
  const router = useRouter();

  // not logged in, push to login page
  React.useEffect(() => {
    if (!isPending && !('userTeam' in authData) && !('user' in authData)) {
      void router.push({ pathname: '/login', query: { redirect: router.asPath } });
    }
  }, [authData, isPending, router]);

  // not logged in, push to login page
  React.useEffect(() => {
    if (!isPending && authData.user == null) {
      void router.push({ pathname: '/login', query: { redirect: router.asPath } });
    }
  }, [authData.user, isPending, router]);

  if (isPending) {
    return <WaveSpinnerContainer message="Authenticating" />;
  }

  if (authData.user == null) {
    return <WaveSpinnerContainer message="Not logged in, sending to login page" />;
  }

  if (!authData.user.isSuper) {
    return <WaveSpinnerContainer message="Not Admin" />;
  }

  if (authData.userTeam != null) {
    return props.children({ ...authData.userTeam, isSuper: authData.user.isSuper });
  }

  // user is authenticated but not part of a team
  return <TeamPicker />;
};

/**
 * forces the page to only show up if not logged in
 * redirects out to provided path defined by `redirect` prop or /leeds
 *
 * @param param0
 * @returns
 */
export const WithoutAuthPage = ({ children, redirect }: RedirectableProps) => {
  const [{ user, isPending: isLoading }] = useAuthContext();
  const router = useRouter();

  React.useEffect(() => {
    if (!isLoading && user != null) {
      // adding this execution to another tick to allow other
      // components to change redirect in query after login/signup
      setTimeout(() => {
        const _redirect = redirect || router.query.redirect;
        const redirectRoute =
          _redirect && typeof _redirect === 'string' && _redirect != '/logout' ? _redirect : '/leads';
        void router.push(redirectRoute);
      }, 0);
    }
  }, [user, isLoading, router, redirect]);

  if (isLoading) {
    return <>{children}</>;
  }

  if (user == null) {
    return <>{children}</>;
  } else {
    return null;
  }
};

const TeamPicker = () => {
  const allTeams = api.team.all.useQuery({});
  const teamCreateMutation = useTeamCreateMutation();
  const router = useRouter();

  if (allTeams.isPending) {
    return <WaveSpinnerContainer message="Loading teams" />;
  }

  const onCreate = async () => {
    await teamCreateMutation.mutateAsync({
      makeDefault: true,
      data: { name: 'My Poseidon Team' },
    });
    void router.push('/teams/active');
  };

  const teams = allTeams.data;

  const activeTeams = teams?.filter((t) => t.userTeamStatus == 'active' || t.userTeamStatus == 'admin');

  const hasTeams = (activeTeams?.length ?? 0) > 0;
  return (
    <>
      <PageHeader icon={<General.Settings01 />} title={hasTeams ? 'Please select your team' : 'Please create a team'}>
        <ContentDivider>
          <Breadcrumbs showStartingChevron>
            <Breadcrumb>Teams</Breadcrumb>
          </Breadcrumbs>
        </ContentDivider>
      </PageHeader>

      <PaddedScrollable>
        <ContentSection>
          {teams && teams.length > 0 && <TeamsTable teams={teams} isLoading={allTeams.isPending} />}
          {activeTeams && activeTeams.length <= 0 && <Button onClick={onCreate}>Create a default team</Button>}
        </ContentSection>
        <ContentSection>
          <ContentDivider icon={<Alerts.AlertTriangle />}>Other actions</ContentDivider>
          <div className="flex flex-row items-start gap-2">
            <ButtonLink text icon={<General.LogOut05 />} href="/logout" type="reset">
              Log out
            </ButtonLink>
          </div>
        </ContentSection>
      </PaddedScrollable>
    </>
  );
};
